// src/Hooks/useCategoriesManage.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  writeBatch,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../Firebase/firebase';

export function useCategoriesManage() {
  // --- STATE: UI ---
  const [activeAction, setActiveAction] = useState('VIEW'); // VIEW | EDIT_MODE
  const [loading, setLoading] = useState(true);

  // --- STATE: SELECTION & FOCUS ---
  const [selectedCatIds, setSelectedCatIds] = useState([]);
  const [focusedRowId, setFocusedRowId] = useState(null);

  // --- STATE: DATA ---
  const [dbRows, setDbRows] = useState([]); // Raw from DB
  const [newRows, setNewRows] = useState([]); // Local only
  const [edits, setEdits] = useState({}); // { id: { field: value } }

  // --- STATE: ALERTS & DIALOGS ---
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  // NEW: Error Alert State (Replaces native alert)
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // NEW: Usage Block Alert (Replaces Migration Dialog)
  const [showUsageBlockAlert, setShowUsageBlockAlert] = useState(false);
  const [usageCount, setUsageCount] = useState(0);

  // --- STATE: INTERNAL ---
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  // --- 1. FIRESTORE SUBSCRIPTION ---
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDbRows(fetchedData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching categories:', error);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);

  // --- 2. DATA PROCESSING (Memoized) ---
  const displayCategories = useMemo(() => {
    // Merge DB rows with Edits
    const mergedDbRows = dbRows.map((row) => {
      if (edits[row.id]) {
        return { ...row, ...edits[row.id] };
      }
      return row;
    });

    // Filter out DB rows that might be in the "newRows" ID set
    const newRowIds = new Set(newRows.map((r) => r.id));
    const finalDbRows = mergedDbRows.filter((r) => !newRowIds.has(r.id));

    // AMENDED: Prevent auto-sorting while actively editing a row to stop jumping
    if (!focusedRowId) {
      finalDbRows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      finalDbRows.sort((a, b) => {
        const origA = dbRows.find((r) => r.id === a.id)?.name || '';
        const origB = dbRows.find((r) => r.id === b.id)?.name || '';
        return origA.localeCompare(origB);
      });
    }

    return [...finalDbRows, ...newRows];
  }, [dbRows, edits, newRows, focusedRowId]);

  const checkIsDirty = useCallback(() => {
    return newRows.length > 0 || Object.keys(edits).length > 0;
  }, [newRows, edits]);

  // --- 3. HELPER ACTIONS ---

  const resetInterface = () => {
    setActiveAction('VIEW');
    setSelectedCatIds([]);
    setFocusedRowId(null);
    setNewRows([]);
    setEdits({});
    setShowUnsavedAlert(false);
    setShowDeleteAlert(false);
    setShowUsageBlockAlert(false);
    setShowErrorAlert(false);
    setErrorMessage('');
    setPendingDeleteIds([]);
  };

  /**
   * AMENDED: Multiple Selection Logic
   * Toggles the ID in the selection array rather than replacing it.
   */
  const handleCheckboxChange = (id) => {
    setSelectedCatIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id); // Remove if exists
      }
      return [...prev, id]; // Add if not exists
    });
  };

  const handleFieldChange = (id, field, val) => {
    if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');

    const isNewRow = newRows.some((r) => r.id === id);

    if (isNewRow) {
      setNewRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: val } : row)),
      );
    } else {
      setEdits((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [field]: val },
      }));
    }
  };

  // --- 4. CORE ACTIONS (ADD, SAVE, DELETE) ---

  const handleAddNew = () => {
    const newId = Date.now().toString();
    const newRow = { id: newId, name: '', isArchived: false };
    setNewRows((prev) => [...prev, newRow]);
    setActiveAction('EDIT_MODE');
    setFocusedRowId(newId);
  };

  const handleSave = async () => {
    if (!checkIsDirty()) return;

    try {
      const batch = writeBatch(db);

      // A. Save New Rows
      newRows.forEach((row) => {
        const docRef = doc(db, 'categories', row.id);
        batch.set(docRef, {
          name: row.name,
          isArchived: row.isArchived || false,
        });
      });

      // B. Save Edits to Existing Rows
      Object.keys(edits).forEach((id) => {
        const docRef = doc(db, 'categories', id);
        batch.update(docRef, edits[id]);
      });

      await batch.commit();
      resetInterface();
    } catch (error) {
      console.error('Error saving batch:', error);
      // REPLACED NATIVE ALERT with State
      setErrorMessage('Failed to save changes. Check console for details.');
      setShowErrorAlert(true);
    }
  };

  const prepareDelete = async () => {
    // 1. Identify Targets (Selection OR Focused Row)
    let targets = [...selectedCatIds];
    if (targets.length === 0 && focusedRowId) targets = [focusedRowId];
    if (targets.length === 0) return;

    // 2. Separate Local (Unsaved) vs DB Rows
    const localIdsToDelete = [];
    const dbIdsToDelete = [];

    targets.forEach((id) => {
      if (newRows.some((r) => r.id === id)) {
        localIdsToDelete.push(id);
      } else {
        dbIdsToDelete.push(id);
      }
    });

    // 3. Immediately remove Local Rows
    if (localIdsToDelete.length > 0) {
      setNewRows((prev) => prev.filter((r) => !localIdsToDelete.includes(r.id)));
      setSelectedCatIds((prev) => prev.filter((id) => !localIdsToDelete.includes(id)));
    }

    // 4. Check Usage for DB Rows
    if (dbIdsToDelete.length > 0) {
      setLoading(true);
      try {
        // AMENDED: Usage check points to markerGroups
        const markersRef = collection(db, 'markerGroups');
        let totalUsage = 0;

        // AMENDED: Chunk queries to avoid Firestore "in" clause limit of 10
        const chunkSize = 10;
        for (let i = 0; i < dbIdsToDelete.length; i += chunkSize) {
          const chunk = dbIdsToDelete.slice(i, i + chunkSize);
          const q = query(markersRef, where('categoryId', 'in', chunk));
          const snapshot = await getDocs(q);
          totalUsage += snapshot.size;
        }

        setLoading(false);
        setPendingDeleteIds(dbIdsToDelete);

        if (totalUsage > 0) {
          // Used -> BLOCK DELETE (Show Archive Alert)
          setUsageCount(totalUsage);
          setShowUsageBlockAlert(true);
        } else {
          // Not Used -> Standard Delete Confirm
          setShowDeleteAlert(true);
        }
      } catch (err) {
        console.error('Error checking usage:', err);
        setLoading(false);
        setPendingDeleteIds(dbIdsToDelete);
        setShowDeleteAlert(true);
      }
    } else {
      setFocusedRowId(null);
    }
  };

  const executeDelete = async () => {
    if (pendingDeleteIds.length === 0) return;

    const batch = writeBatch(db);
    pendingDeleteIds.forEach((id) => {
      batch.delete(doc(db, 'categories', id));
    });

    try {
      await batch.commit();

      setEdits((prev) => {
        const copy = { ...prev };
        pendingDeleteIds.forEach((id) => delete copy[id]);
        return copy;
      });
      setSelectedCatIds((prev) => prev.filter((id) => !pendingDeleteIds.includes(id)));
      setFocusedRowId(null);
      setPendingDeleteIds([]);
      setShowDeleteAlert(false);
    } catch (err) {
      console.error('Delete failed', err);
      // Added error handling here as well
      setErrorMessage('Delete failed. Check console for details.');
      setShowErrorAlert(true);
    }
  };

  // --- 5. HEADER CONTROLLER ---
  const handleHeaderAction = (action) => {
    switch (action) {
      case 'ADD_NEW':
        return handleAddNew();
      case 'SAVE':
        return handleSave();
      case 'DELETE':
        return prepareDelete();
      case 'CLOSE':
        if (checkIsDirty()) setShowUnsavedAlert(true);
        else resetInterface();
        return;
      default:
        return;
    }
  };

  // --- 6. EXPOSE ---
  return {
    state: {
      loading,
      displayCategories,
      selectedCatIds,
      focusedRowId,
      activeAction,
      newRows,
    },
    flags: {
      isDirty: checkIsDirty(),
      canSave: checkIsDirty(),
      canDelete: selectedCatIds.length > 0 || focusedRowId !== null,
      canClose: checkIsDirty(),
    },
    alerts: {
      showDelete: showDeleteAlert,
      setShowDelete: setShowDeleteAlert,
      showUnsaved: showUnsavedAlert,
      setShowUnsaved: setShowUnsavedAlert,
      showUsageBlock: showUsageBlockAlert,
      setShowUsageBlock: setShowUsageBlockAlert,
      // Expose Error Alert
      showError: showErrorAlert,
      setShowError: setShowErrorAlert,
      errorMessage: errorMessage,
    },
    usage: {
      count: usageCount,
    },
    actions: {
      handleHeaderAction,
      handleCheckboxChange,
      handleFieldChange,
      handleConfirmDelete: executeDelete,
      handleCloseConfirmed: resetInterface,
      setFocusedRowId,
    },
  };
}
